/**
 * DEMO SERVICE - INTENTIONALLY VULNERABLE CODE
 * 
 * ⚠️  SECURITY WARNING: This file contains intentionally vulnerable code
 *     for demonstration purposes only. This code should NEVER be:
 *     - Served as static assets
 *     - Accessible via HTTP requests
 *     - Used in production environments
 * 
 * This service creates demo ZIP files with known vulnerabilities
 * to showcase SafeVibe's security scanning capabilities.
 */

/**
 * Demo Service - Handles the "Try Demo Code" workflow
 * Creates a demo project with intentional security issues for demonstration
 */

// Demo project files with intentional security issues
const demoFiles = {
  'package.json': JSON.stringify({
    "name": "demo-vulnerable-app",
    "version": "1.0.0",
    "description": "Demo app with security issues for SafeVibe scanning",
    "main": "index.js",
    "scripts": {
      "start": "node index.js",
      "dev": "nodemon index.js"
    },
    "dependencies": {
      "express": "^4.18.0",
      "supabase": "^1.0.0",
      "openai": "^4.0.0"
    }
  }, null, 2),

  'index.js': `// Demo Express app with security vulnerabilities
const express = require('express');
const app = express();

// 🚨 SECURITY ISSUE: Hardcoded API keys
const OPENAI_API_KEY = "sk-1234567890abcdef1234567890abcdef1234567890abcdef";
const SUPABASE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4YW1wbGUiLCJyb2xlIjoiYW5vbiIsImlhdCI6MTY0MjU0ODAwMCwiZXhwIjoxOTU4MTI0MDAwfQ.example";

// 🚨 SECURITY ISSUE: No authentication on sensitive endpoint
app.get('/admin/users', (req, res) => {
  // This should require admin authentication
  res.json({ users: ['admin@example.com', 'user@example.com'] });
});

// 🚨 SECURITY ISSUE: Logging sensitive data
app.post('/login', (req, res) => {
  const { email, password } = req.body;
  console.log('Login attempt:', { email, password }); // Logs password!
  res.json({ success: true });
});

// 🚨 SECURITY ISSUE: No CORS protection
app.use((req, res, next) => {
  res.header('Access-Control-Allow-Origin', '*');
  res.header('Access-Control-Allow-Headers', '*');
  next();
});

app.listen(3000, () => {
  console.log('Server running on port 3000');
});`,

  '.env': `# 🚨 SECURITY ISSUE: Secrets in .env file that might be committed
OPENAI_API_KEY=sk-1234567890abcdef1234567890abcdef1234567890abcdef
SUPABASE_URL=https://example.supabase.co
SUPABASE_ANON_KEY=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4YW1wbGUiLCJyb2xlIjoiYW5vbiIsImlhdCI6MTY0MjU0ODAwMCwiZXhwIjoxOTU4MTI0MDAwfQ.example
DATABASE_PASSWORD=super_secret_password_123
STRIPE_SECRET_KEY=sk_test_1234567890abcdef1234567890abcdef12345678`,

  'supabase-config.js': `// 🚨 SECURITY ISSUE: No Row Level Security
export const supabaseConfig = {
  url: 'https://example.supabase.co',
  anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV4YW1wbGUiLCJyb2xlIjoiYW5vbiIsImlhdCI6MTY0MjU0ODAwMCwiZXhwIjoxOTU4MTI0MDAwfQ.example',
  // 🚨 RLS disabled - anyone can read/write all data
  enableRLS: false
};

// 🚨 SECURITY ISSUE: Public bucket configuration
export const storageConfig = {
  bucketName: 'user-uploads',
  isPublic: true, // This makes all uploads publicly accessible!
};`,

  'README.md': `# Demo Vulnerable App

This is a demo application created to showcase security vulnerabilities that SafeVibe can detect.

## Known Issues (for demo purposes):
- Hardcoded API keys in source code
- No authentication on admin endpoints  
- Logging sensitive user data
- Disabled Row Level Security
- Public storage buckets
- Missing privacy policy and terms

⚠️ **DO NOT USE IN PRODUCTION** - This code contains intentional security vulnerabilities.`,

  'client.js': `// Frontend code with privacy issues
function trackUser(userData) {
  // 🚨 PRIVACY ISSUE: Sending PII to analytics
  analytics.track('user_action', {
    email: userData.email,
    fullName: userData.name,
    phoneNumber: userData.phone,
    ipAddress: userData.ip
  });
}

// 🚨 SECURITY ISSUE: Token stored in localStorage
function saveAuthToken(token) {
  localStorage.setItem('auth_token', token);
  // Should use httpOnly cookies instead
}

// 🚨 SECURITY ISSUE: No input validation
function updateProfile(userInput) {
  document.getElementById('profile').innerHTML = userInput; // XSS vulnerability
}`
};

/**
 * Creates a demo ZIP file with vulnerable code
 */
export async function createDemoProject(): Promise<File> {
  console.log('[DemoService] Creating demo project ZIP...');
  
  // Dynamically import JSZip to avoid bundle size issues
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  // Add all demo files to the ZIP
  Object.entries(demoFiles).forEach(([filename, content]) => {
    zip.file(filename, content);
  });

  // Generate the ZIP file
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const zipFile = new File([zipBlob], 'demo-vulnerable-app.zip', { type: 'application/zip' });

  console.log('[DemoService] Demo project ZIP created:', {
    filename: zipFile.name,
    size: zipFile.size,
    fileCount: Object.keys(demoFiles).length
  });

  return zipFile;
}

/**
 * Triggers the demo scan workflow
 */
export async function triggerDemoScan(): Promise<void> {
  console.log('[DemoService] Starting demo scan workflow...');
  
  try {
    // Create the demo project
    const demoFile = await createDemoProject();
    
    // Store demo indicator in localStorage
    localStorage.setItem('demo_scan_active', 'true');
    localStorage.setItem('demo_file_name', demoFile.name);
    
    // Navigate to upload screen with demo parameter
    // Use URL param to clearly indicate this is a demo flow
    const uploadUrl = `/upload?demo=true&source=demo-button`;
    window.location.href = uploadUrl;
    
  } catch (error) {
    console.error('[DemoService] Failed to trigger demo scan:', error);
    throw new Error('Failed to start demo scan. Please try again.');
  }
}

/**
 * Checks if current session is a demo scan
 */
export function isDemoScan(): boolean {
  return localStorage.getItem('demo_scan_active') === 'true';
}

/**
 * Clears demo scan state
 */
export function clearDemoScan(): void {
  localStorage.removeItem('demo_scan_active');
  localStorage.removeItem('demo_file_name');
  console.log('[DemoService] Demo scan state cleared');
}

/**
 * Gets expected demo scan results for immediate display
 */
export function getDemoScanResults() {
  return [
    {
      id: 'demo-1',
      type: 'secret_leak',
      severity: 'critical',
      title: 'Hardcoded OpenAI API Key',
      description: 'Found exposed OpenAI API key in source code',
      file: 'index.js',
      line: 5,
      code: 'const OPENAI_API_KEY = "sk-1234567890abcdef..."',
      fix: 'Move API keys to environment variables and use process.env.OPENAI_API_KEY',
      impact: 'Anyone with access to your code can use your OpenAI credits. This could result in unexpected charges.'
    },
    {
      id: 'demo-2', 
      type: 'auth_bypass',
      severity: 'high',
      title: 'Unauthenticated Admin Endpoint',
      description: 'Admin endpoint accessible without authentication',
      file: 'index.js',
      line: 8,
      code: "app.get('/admin/users', (req, res) => {",
      fix: 'Add authentication middleware to protect admin routes',
      impact: 'Attackers can access sensitive user data without proper authorization.'
    },
    {
      id: 'demo-3',
      type: 'privacy_leak',
      severity: 'high', 
      title: 'Password Logged in Plain Text',
      description: 'User passwords are being logged to console',
      file: 'index.js',
      line: 15,
      code: "console.log('Login attempt:', { email, password });",
      fix: 'Remove password from logs or hash before logging',
      impact: 'User passwords could be exposed in log files, violating privacy and security.'
    },
    {
      id: 'demo-4',
      type: 'cors_misconfiguration',
      severity: 'medium',
      title: 'Overly Permissive CORS',
      description: 'CORS allows all origins with wildcard',
      file: 'index.js', 
      line: 20,
      code: "res.header('Access-Control-Allow-Origin', '*');",
      fix: 'Specify allowed origins explicitly instead of using wildcard',
      impact: 'Any website can make requests to your API, potentially enabling CSRF attacks.'
    },
    {
      id: 'demo-5',
      type: 'rls_disabled',
      severity: 'critical',
      title: 'Row Level Security Disabled',
      description: 'Supabase RLS is disabled, allowing unrestricted data access',
      file: 'supabase-config.js',
      line: 6,
      code: 'enableRLS: false',
      fix: 'Enable Row Level Security and create appropriate policies',
      impact: 'Any user can read or modify any data in your database.'
    },
    {
      id: 'demo-6',
      type: 'public_bucket',
      severity: 'high',
      title: 'Public Storage Bucket',
      description: 'Storage bucket is configured as public',
      file: 'supabase-config.js',
      line: 11,
      code: 'isPublic: true',
      fix: 'Set bucket to private and implement proper access controls',
      impact: 'All uploaded files are publicly accessible to anyone with the URL.'
    },
    {
      id: 'demo-7',
      type: 'pii_tracking',
      severity: 'medium',
      title: 'PII Sent to Analytics',
      description: 'Personal information being tracked without consent',
      file: 'client.js',
      line: 4,
      code: 'email: userData.email, fullName: userData.name',
      fix: 'Remove PII from analytics or get explicit user consent',
      impact: 'Violates privacy regulations like GDPR. Could result in fines.'
    },
    {
      id: 'demo-8',
      type: 'xss_vulnerability',
      severity: 'high',
      title: 'XSS Vulnerability',
      description: 'User input directly inserted into DOM without sanitization',
      file: 'client.js',
      line: 15,
      code: "document.getElementById('profile').innerHTML = userInput;",
      fix: 'Use textContent instead of innerHTML or sanitize input',
      impact: 'Attackers can inject malicious scripts that execute in user browsers.'
    }
  ];
} 